<template>
  <div class="contract-sandbox">
    <v-toolbar density="compact" color="info">
      <v-toolbar-title class="text-body-2">
        Sandbox de Testes
      </v-toolbar-title>
      
      <v-spacer></v-spacer>
      
      <v-btn
        variant="text"
        size="small"
        prepend-icon="mdi-refresh"
        @click="resetSandbox"
      >
        Reiniciar
      </v-btn>
    </v-toolbar>
    
    <div class="sandbox-container">
      <template v-if="!contractDeployed">
        <div class="d-flex flex-column align-center justify-center pa-6 empty-sandbox">
          <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-test-tube</v-icon>
          <div class="text-h6 mb-2">Sandbox Vazio</div>
          <div class="text-body-2 text-center mb-4 text-medium-emphasis">
            Compile e implante seu contrato para testar suas funcionalidades em um ambiente seguro.
          </div>
          <v-btn
            color="primary"
            prepend-icon="mdi-rocket-launch"
            @click="deploySandbox"
            :loading="deploying"
            :disabled="!isCodeCompiled"
          >
            Implantar no Sandbox
          </v-btn>
        </div>
      </template>
      
      <template v-else>
        <div class="px-4 py-3 bg-success-lighten-5 d-flex align-center">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          <div>
            <div class="font-weight-medium">Contrato implantado com sucesso!</div>
            <div class="text-caption">Endereço: {{ contractAddress }}</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            variant="outlined"
            size="small"
            @click="resetSandbox"
          >
            Limpar Sandbox
          </v-btn>
        </div>
        
        <v-divider></v-divider>
        
        <div class="d-flex flex-column px-4 py-3">
          <div class="text-body-1 font-weight-medium mb-2">Métodos do Contrato</div>
          
          <div v-if="contractMethods.length === 0" class="text-center py-4 text-medium-emphasis">
            Este contrato não possui métodos públicos para chamar.
          </div>
          
          <div v-else>
            <v-expansion-panels variant="accordion">
              <v-expansion-panel
                v-for="method in contractMethods"
                :key="method.name"
                :title="method.name"
                :subtitle="getMethodDescription(method)"
                :disabled="method.disabled"
              >
                <template v-slot:text>
                  <v-form @submit.prevent="callMethod(method)">
                    <div v-if="method.inputs.length > 0">
                      <div v-for="(input, i) in method.inputs" :key="i" class="mb-3">
                        <v-text-field
                          v-model="method.inputValues[i]"
                          :label="input.name + ' (' + input.type + ')'"
                          :hint="getInputHint(input.type)"
                          persistent-hint
                          variant="outlined"
                          density="compact"
                        ></v-text-field>
                      </div>
                    </div>
                    
                    <div v-if="method.payable" class="mb-3">
                      <v-text-field
                        v-model="method.value"
                        label="Valor (QRX)"
                        type="number"
                        hint="Quantidade de QRX a enviar"
                        persistent-hint
                        variant="outlined"
                        density="compact"
                      ></v-text-field>
                    </div>
                    
                    <div class="d-flex justify-end mt-2">
                      <v-btn
                        :color="method.mutating ? 'warning' : 'primary'"
                        type="submit"
                        :loading="method.loading"
                        :prepend-icon="method.mutating ? 'mdi-pencil' : 'mdi-eye'"
                      >
                        {{ method.mutating ? 'Executar' : 'Consultar' }}
                      </v-btn>
                    </div>
                    
                    <div v-if="method.lastResult" class="mt-3 pa-3 rounded" :class="method.lastError ? 'bg-error-lighten-5' : 'bg-primary-lighten-5'">
                      <div class="text-caption mb-1">Resultado:</div>
                      <div v-if="method.lastError" class="text-error">
                        <v-icon size="small" color="error" class="mr-1">mdi-alert-circle</v-icon>
                        {{ method.lastResult }}
                      </div>
                      <div v-else class="text-pre-wrap">{{ formatResult(method.lastResult) }}</div>
                    </div>
                  </v-form>
                </template>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
        
        <v-divider></v-divider>
        
        <div class="px-4 py-3">
          <div class="text-body-1 font-weight-medium mb-2">Contas de Teste</div>
          
          <v-row>
            <v-col cols="12" md="6" v-for="(account, index) in testAccounts" :key="account.address">
              <v-card variant="outlined" :class="{'border-primary': account.selected}">
                <v-card-text class="pa-2">
                  <div class="d-flex align-center">
                    <v-avatar size="32" :color="getAccountColor(index)" class="mr-2">
                      <span class="text-white">{{ index + 1 }}</span>
                    </v-avatar>
                    
                    <div class="flex-grow-1">
                      <div class="text-caption text-medium-emphasis">{{ t('accounts.address') }}</div>
                      <div class="text-truncate">{{ account.address }}</div>
                    </div>
                    
                    <v-chip :color="getAccountColor(index)" size="small" class="ml-2">
                      {{ account.balance }} QRX
                    </v-chip>
                  </div>
                </v-card-text>
                
                <v-divider></v-divider>
                
                <v-card-actions class="pa-2">
                  <v-spacer></v-spacer>
                  <v-btn
                    size="small"
                    :variant="account.selected ? 'flat' : 'outlined'"
                    :color="account.selected ? 'primary' : undefined"
                    @click="selectAccount(index)"
                  >
                    {{ account.selected ? 'Selecionada' : 'Selecionar' }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </div>
        
        <v-divider></v-divider>
        
        <div class="px-4 py-3">
          <div class="text-body-1 font-weight-medium mb-2">Logs de Transações</div>
          
          <div v-if="transactionLogs.length === 0" class="text-center py-4 text-medium-emphasis">
            Nenhuma transação executada ainda.
          </div>
          
          <v-timeline v-else density="compact" line-color="primary" class="transaction-timeline">
            <v-timeline-item
              v-for="(log, index) in transactionLogs"
              :key="index"
              :dot-color="log.success ? 'success' : 'error'"
              size="small"
            >
              <div class="d-flex align-center mb-1">
                <span class="font-weight-medium">{{ log.method }}</span>
                <v-chip size="x-small" :color="log.txType === 'call' ? 'primary' : 'warning'" class="ml-2">
                  {{ log.txType === 'call' ? 'CALL' : 'TX' }}
                </v-chip>
              </div>
              
              <div class="text-caption d-flex">
                <span>{{ formatTimestamp(log.timestamp) }}</span>
                <span class="mx-1">•</span>
                <span>Conta #{{ log.accountIndex + 1 }}</span>
              </div>
              
              <div v-if="!log.success" class="text-error text-caption mt-1">
                {{ log.error }}
              </div>
            </v-timeline-item>
          </v-timeline>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ContractSandbox',
  props: {
    compiledCode: {
      type: Object,
      default: () => null
    },
    isCodeCompiled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      contractDeployed: false,
      deploying: false,
      contractAddress: 'Q0x7f8a987c54321b1a2c3d4e5f67890abcdef1234',
      contractMethods: [],
      testAccounts: [
        {
          address: 'Q0x1234567890abcdef1234567890abcdef12345678',
          balance: 1000,
          selected: true
        },
        {
          address: 'Q0x2345678901abcdef2345678901abcdef23456789',
          balance: 500,
          selected: false
        },
        {
          address: 'Q0x3456789012abcdef3456789012abcdef34567890',
          balance: 250,
          selected: false
        }
      ],
      transactionLogs: []
    }
  },
  methods: {
    resetSandbox() {
      this.contractDeployed = false
      this.contractMethods = []
      this.transactionLogs = []
      this.$emit('sandbox-reset')
    },
    deploySandbox() {
      if (!this.isCodeCompiled) {
        return
      }
      
      this.deploying = true
      
      // Simulação de implantação (em produção, isso interagiria com o backend)
      setTimeout(() => {
        this.deploying = false
        this.contractDeployed = true
        
        // Gera métodos do contrato baseados no código compilado
        this.extractContractMethods()
        
        // Adiciona log de implantação
        this.transactionLogs.unshift({
          method: '<Deploy>',
          txType: 'tx',
          timestamp: Date.now(),
          accountIndex: this.getSelectedAccountIndex(),
          success: true
        })
        
        this.$emit('contract-deployed', {
          address: this.contractAddress,
          timestamp: new Date().toISOString()
        })
      }, 1500)
    },
    extractContractMethods() {
      // Em uma implementação real, isso analisaria o ABI do contrato compilado
      // Aqui, criamos alguns métodos de exemplo baseados em contratos comuns
      
      this.contractMethods = [
        {
          name: 'getName',
          description: 'Retorna o nome do contrato',
          inputs: [],
          inputValues: [],
          outputs: [{ type: 'string' }],
          mutating: false,
          payable: false,
          loading: false,
          lastResult: null,
          lastError: false
        },
        {
          name: 'getBalance',
          description: 'Retorna o saldo do endereço fornecido',
          inputs: [{ name: 'address', type: 'address' }],
          inputValues: [],
          outputs: [{ type: 'uint256' }],
          mutating: false,
          payable: false,
          loading: false,
          lastResult: null,
          lastError: false
        },
        {
          name: 'transfer',
          description: 'Transfere tokens para o endereço fornecido',
          inputs: [
            { name: 'to', type: 'address' },
            { name: 'amount', type: 'uint256' }
          ],
          inputValues: [],
          outputs: [{ type: 'bool' }],
          mutating: true,
          payable: false,
          loading: false,
          lastResult: null,
          lastError: false
        },
        {
          name: 'deposit',
          description: 'Deposita QRX no contrato',
          inputs: [],
          inputValues: [],
          outputs: [],
          mutating: true,
          payable: true,
          value: '',
          loading: false,
          lastResult: null,
          lastError: false
        }
      ]
    },
    getMethodDescription(method) {
      let result = method.mutating ? '(Mutável) ' : '(Visualização) '
      
      if (method.inputs.length > 0) {
        const params = method.inputs.map(input => `${input.type} ${input.name}`).join(', ')
        result += `(${params})`
      } else {
        result += '()'
      }
      
      if (method.outputs && method.outputs.length > 0) {
        const returnType = method.outputs.map(output => output.type).join(', ')
        result += ` → ${returnType}`
      }
      
      if (method.payable) {
        result += ' 💰 Aceita QRX'
      }
      
      return result
    },
    getInputHint(type) {
      if (type === 'address') {
        return 'Endereço no formato Q0x...'
      } else if (type === 'uint256' || type === 'uint' || type === 'int') {
        return 'Valor numérico'
      } else if (type === 'bool') {
        return 'true ou false'
      } else if (type === 'string') {
        return 'Texto'
      } else {
        return type
      }
    },
    callMethod(method) {
      method.loading = true
      method.lastResult = null
      method.lastError = false
      
      // Simulação de chamada de método (em produção, isso interagiria com o backend)
      setTimeout(() => {
        method.loading = false
        
        // Lógica simulada para diferentes métodos
        try {
          if (method.name === 'getName') {
            method.lastResult = 'QueenToken'
          } else if (method.name === 'getBalance') {
            const address = method.inputValues[0]
            if (!address || !address.startsWith('Q0x')) {
              throw new Error('Endereço inválido. Deve começar com Q0x')
            }
            // Retorna um valor aleatório para simular o saldo
            method.lastResult = 0 // Resultado real virá da API
          } else if (method.name === 'transfer') {
            const to = method.inputValues[0]
            const amount = parseFloat(method.inputValues[1])
            
            if (!to || !to.startsWith('Q0x')) {
              throw new Error('Endereço de destino inválido')
            }
            
            if (isNaN(amount) || amount <= 0) {
              throw new Error('Quantidade inválida')
            }
            
            // Simulação de transferência bem-sucedida
            method.lastResult = true
          } else if (method.name === 'deposit') {
            const value = parseFloat(method.value)
            
            if (isNaN(value) || value <= 0) {
              throw new Error('Valor de QRX inválido')
            }
            
            // Simulação de depósito bem-sucedido
            method.lastResult = 'Depósito realizado com sucesso'
          }
          
          // Adiciona log de transação
          this.transactionLogs.unshift({
            method: method.name,
            txType: method.mutating ? 'tx' : 'call',
            timestamp: Date.now(),
            accountIndex: this.getSelectedAccountIndex(),
            success: true
          })
        } catch (error) {
          method.lastResult = error.message
          method.lastError = true
          
          // Adiciona log de erro
          this.transactionLogs.unshift({
            method: method.name,
            txType: method.mutating ? 'tx' : 'call',
            timestamp: Date.now(),
            accountIndex: this.getSelectedAccountIndex(),
            success: false,
            error: error.message
          })
        }
      }, 1000)
    },
    formatResult(result) {
      if (typeof result === 'object') {
        return JSON.stringify(result, null, 2)
      } else {
        return String(result)
      }
    },
    selectAccount(index) {
      this.testAccounts.forEach((account, i) => {
        account.selected = i === index
      })
    },
    getSelectedAccountIndex() {
      return this.testAccounts.findIndex(account => account.selected)
    },
    getAccountColor(index) {
      const colors = ['primary', 'purple', 'green', 'orange', 'blue']
      return colors[index % colors.length]
    },
    formatTimestamp(timestamp) {
      return new Date(timestamp).toLocaleTimeString()
    }
  },
  watch: {
    isCodeCompiled(newVal) {
      // Se o contrato estiver implantado e o código for recompilado, precisamos atualizar
      if (this.contractDeployed && newVal) {
        this.resetSandbox()
      }
    }
  }
}
</script>

<style scoped>
.contract-sandbox {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-radius: 4px;
}

.sandbox-container {
  flex: 1;
  overflow-y: auto;
  background-color: rgb(var(--v-theme-surface));
}

.empty-sandbox {
  min-height: 300px;
}

.text-pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
}

.transaction-timeline {
  max-height: 300px;
  overflow-y: auto;
}
</style>
